/**
 * Product Siblings Variants
 * Handles color swatch clicks for sibling products
 */

class ProductSiblingsVariants {
  constructor(container) {
    this.container = container;
    this.section = container.closest('[data-section-type]') || container.closest('[data-section-id]');
    this.productForm = document.querySelector('product-form');
    this.isUpdating = false;
    
    this.init();
  }

  getProductJsonElement() {
    if (this.section) {
      const scoped = this.section.querySelector('[data-product-json]');
      if (scoped) return scoped;
    }
    return document.querySelector('[data-product-json]');
  }

  init() {
    this.attachEventListeners();
    this.updateActiveStates();
    this.hideUnavailableColors();
    // Disabled: do not auto-redirect to an available variant on load
    // this.redirectToAvailableVariant();
    this.updateInitialSizeAvailability();
    this.updateInitialVariantUI();
  }

  attachEventListeners() {
    const hasSiblingColorSwatches =
      this.container.querySelectorAll('[data-current-product] [data-sibling-color-swatch] input[data-sibling-color-input]').length > 0;

    const activateColorSwatch = (swatch) => {
      if (!swatch || this.isUpdating) return;
      this.handleColorSwatchClick(swatch);
    };

    // iOS Safari often never fires click on clipped radios inside <radio-swatch>.
    // Price must also update from change/touchend, not click alone.
    this.container.addEventListener('click', (e) => {
      const swatch = e.target.closest('[data-sibling-color-swatch]');
      if (!swatch) return;
      e.preventDefault();
      e.stopPropagation();
      activateColorSwatch(swatch);
    });

    this.container.addEventListener('change', (e) => {
      const input = e.target.closest('[data-sibling-color-input]');
      if (!input) return;
      activateColorSwatch(input.closest('[data-sibling-color-swatch]'));
    });

    let touchStartX = 0;
    let touchStartY = 0;
    this.container.addEventListener('touchstart', (e) => {
      const touch = e.changedTouches && e.changedTouches[0];
      if (!touch) return;
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
    }, {passive: true});

    this.container.addEventListener('touchend', (e) => {
      const swatch = e.target.closest('[data-sibling-color-swatch]');
      if (!swatch) return;
      const touch = e.changedTouches && e.changedTouches[0];
      if (touch) {
        const dx = Math.abs(touch.clientX - touchStartX);
        const dy = Math.abs(touch.clientY - touchStartY);
        if (dx > 12 || dy > 12) return;
      }
      activateColorSwatch(swatch);
    }, {passive: true});
    
    // Handle size selector changes (only update price/sku, not availability)
    const sizeContainer = this.section.querySelector('[data-sibling-sizes]');
    if (sizeContainer && hasSiblingColorSwatches) {
      sizeContainer.addEventListener('change', (e) => {
        const sizeInput = e.target.closest('[data-single-option-selector]');
        if (sizeInput && !this.isUpdating) {
          console.log('[Siblings] Size changed to:', sizeInput.value);
          // Prevent the theme's applyHide from running
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.handleSizeChange(sizeInput.value);
        }
      }, true); // Use capture phase to intercept before theme handlers
    }
  }

  handleColorSwatchClick(swatch) {
    const productId = swatch.dataset.siblingProductId;
    const productUrl = swatch.dataset.siblingProductUrl;
    const colorValue = swatch.dataset.colorValue;
    const input = swatch.querySelector('input[data-sibling-color-input]');
    
    if (!input || input.disabled || this.isUpdating) return;

    this.isUpdating = true;
    
    const parentWrapper = swatch.closest('[data-sibling-product]');
    const isCurrentProduct = parentWrapper?.hasAttribute('data-current-product');
    
    if (isCurrentProduct) {
      input.checked = true;
      this.updateMainProductVariant(colorValue);
    } else {
      const siblingVariantId = this.findSiblingVariant(productId, colorValue);
      const url = siblingVariantId ? this.getUrlWithVariant(productUrl, siblingVariantId) : productUrl;
      window.location.href = url;
    }
  }

  handleSizeChange(newSize) {
    this.isUpdating = true;
    
    // Get product data
    const productDataElement = this.getProductJsonElement();
    if (!productDataElement) {
      this.isUpdating = false;
      return;
    }
    
    const productData = JSON.parse(productDataElement.textContent);
    
    // Get current color from a checked color swatch
    const checkedColorSwatch = this.container.querySelector('[data-current-product] [data-sibling-color-swatch] input[data-sibling-color-input]:checked');
    const currentColor = checkedColorSwatch?.value;
    
    if (!currentColor) {
      this.isUpdating = false;
      return;
    }
    
    // Find matching variant (color + size)
    const matchingVariant = productData.variants.find(v => {
      return this.isPricedVariant(v) && v.option1 === currentColor && v.option2 === newSize;
    });
    
    if (matchingVariant) {
      // Update variant ID input
      const variantIdInput = this.section.querySelector('input[name="id"]');
      if (variantIdInput) {
        variantIdInput.value = matchingVariant.id;
      }
      
      // Update URL
      if (window.history.replaceState) {
        const newUrl = this.getUrlWithVariant(window.location.href, matchingVariant.id);
        window.history.replaceState({path: newUrl}, '', newUrl);
      }
      
      // Size changes should not switch the gallery color set.
      // Keep current color images, update variant state/price/SKU only.
      this.updateProductDOM(matchingVariant, productData, false);
      this.emitVariantChange(matchingVariant);
    }
    
    setTimeout(() => { this.isUpdating = false; }, 100);
  }

  updateMainProductVariant(colorValue) {
    this.isUpdating = true;
    
    // Get product data
    const productDataElement = this.getProductJsonElement();
    if (!productDataElement) {
      this.isUpdating = false;
      return;
    }
    
    const productData = JSON.parse(productDataElement.textContent);
    
    // Get current size
    const sizeSelector = this.section.querySelector('[data-sibling-sizes] [data-single-option-selector]:checked') ||
                        this.section.querySelector('[data-sibling-sizes] [data-single-option-selector][type="hidden"]');
    const currentSize = sizeSelector?.value;
    
    // Find matching variant (color + size), then any priced variant of this color
    const matchingVariant = productData.variants.find(v => {
      return this.isPricedVariant(v) && v.option1 === colorValue && (v.option2 === currentSize || !currentSize);
    }) || productData.variants.find(v => this.isPricedVariant(v) && v.option1 === colorValue);
    
    if (matchingVariant) {
      // Update variant ID input
      const variantIdInput = this.section.querySelector('input[name="id"]');
      if (variantIdInput) {
        variantIdInput.value = matchingVariant.id;
      }
      
      // Update URL
      if (window.history.replaceState) {
        const newUrl = this.getUrlWithVariant(window.location.href, matchingVariant.id);
        window.history.replaceState({path: newUrl}, '', newUrl);
      }
      
      // Update sale badge based on variant sale status
      
      // Update DOM with color change flag
      this.updateProductDOM(matchingVariant, productData, true);
      this.emitVariantChange(matchingVariant);
    }
    this.updateActiveStates();
    setTimeout(() => { this.isUpdating = false; }, 100);
  }

  updateProductDOM(variant, productData, isColorChange = false) {
    const onSale = !!(variant.compare_at_price && variant.compare_at_price > variant.price);

    // Update price
    const priceElements = this.section.querySelectorAll('[data-product-price]');
    if (priceElements.length && variant.price != null) {
      const formattedPrice = variant.price === 0
        ? (window.theme?.strings?.free || 'Free')
        : this.formatMoney(variant.price);
      priceElements.forEach((el) => {
        el.innerHTML = formattedPrice;
        el.classList.toggle('product__price--sale', onSale);
      });
    }

    // Update compare price
    const comparePriceElements = this.section.querySelectorAll('[data-compare-price]');
    comparePriceElements.forEach((el) => {
      const compareText = el.parentElement?.querySelector('[data-compare-text]');
      if (onSale) {
        el.innerHTML = this.formatMoney(variant.compare_at_price);
        el.classList.remove('hidden');
        el.style.removeProperty('display');
        compareText?.classList.remove('hidden');
      } else {
        // iOS WebKit can keep <s> strikethrough visible with display:none alone.
        el.innerHTML = '';
        el.classList.add('hidden');
        el.style.display = 'none';
        compareText?.classList.add('hidden');
      }
    });

    this.updateSaleBadge(variant);

    // Toggle sale badge visibility based on variant sale status
    try {
      const saleBadge = this.section.querySelector('[data-sale-badge]') || document.querySelector('[data-sale-badge]');
      const productSaleEl = this.section.querySelector('.product__sale') || document.querySelector('.product__sale');
      if (saleBadge && variant) {
        let isOnSale = false;
        const saleStatusElem = document.querySelector('[data-variant-sale-status]');
        if (saleStatusElem) {
          try {
            const variantsSaleStatus = JSON.parse(saleStatusElem.textContent || '[]');
            const match = variantsSaleStatus.find(v => Number(v.variant_id) === Number(variant.id));
            if (match) isOnSale = match.on_sale === true;
          } catch(e) { /* fallback below */ }
        }
        if (typeof isOnSale !== 'boolean' || saleStatusElem == null) {
          isOnSale = !!(variant.compare_at_price && variant.price && variant.compare_at_price > variant.price);
        }
        saleBadge.classList.toggle('hidden', !isOnSale);
        if (productSaleEl) productSaleEl.classList.toggle('hidden', !isOnSale);
      }
    } catch(e) { /* no-op */ }
    
    // Update SKU
    const skuElements = this.section.querySelectorAll('[data-variant-sku]');
    if (skuElements.length && variant.sku) {
      skuElements.forEach(el => el.textContent = variant.sku);
    }
    
    // Update availability
    const formWrappers = this.section.querySelectorAll('[data-form-wrapper]');
    formWrappers.forEach(wrapper => {
      if (variant.available) {
        wrapper.classList.remove('variant--soldout', 'variant--unavailable');
      } else {
        wrapper.classList.add('variant--soldout');
        wrapper.classList.remove('variant--unavailable');
      }
    });
    
    // Update buttons
    const addToCartButtons = this.section.querySelectorAll('[data-add-to-cart]');
    addToCartButtons.forEach(btn => btn.disabled = !variant.available);
    
    const addToCartTextElements = this.section.querySelectorAll('[data-add-to-cart-text]');
    const buttonText = variant.available 
      ? (window.theme?.strings?.addToCart || 'Add to cart')
      : (window.theme?.strings?.soldOut || 'Sold out');
    addToCartTextElements.forEach(el => el.textContent = buttonText);
    
    // Update images - only when color changes
    if (isColorChange && variant.option1) {
      const colorValue = variant.option1;
      const productImages = this.section.querySelector('product-images');
      
      if (productImages) {
        // Split color value by space and get the last part (color code)
        // e.g., "Sage Stone V822" -> ["Sage", "Stone", "V822"] -> "V822"
        // or "Amber Y263" -> ["Amber", "Y263"] -> "Y263"
        const colorParts = colorValue.split(' ');
        const colorCode = colorParts[colorParts.length - 1];
        const colorCodeLower = String(colorCode).toLowerCase();
        
        console.log('[Siblings] Color value:', colorValue, 'Color code:', colorCode);
        
        // Reset slide state so product-images does not keep the first .media--active in DOM order (mobile fader).
        productImages.querySelectorAll('.product__slide').forEach((slide) => {
          slide.classList.remove('media--active', 'media--hiding');
        });
        
        // Find all image slides. Match data-color or any filename segment so
        // STYLE_TEAM_COLOR_SHOT files (NBA) are not treated as a different color.
        const allImages = productImages.querySelectorAll('[data-type="image"]');
        const slideMatchesColor = (image) => {
          if (typeof mediaMatchesColor === 'function') {
            return mediaMatchesColor(image, colorCodeLower);
          }
          return (image.dataset.color || '').toLowerCase() === colorCodeLower;
        };
        let visibleCount = 0;
        const imagesToShow = Array.from(allImages).filter(slideMatchesColor);
        
        allImages.forEach(image => {
          const imageColor = (image.dataset.color || '').toLowerCase();
          
          if (imageColor === colorCodeLower) {
            // Later photos of this color stay in the fader stack. If they are
            // all unhidden they paint on top of the featured image, and a
            // thumbnail click never looks like it changed the main photo.
            image.classList.add('media--hidden');
            image.classList.remove('media--active', 'media--hiding');
            image.style.display = '';
            visibleCount++;
          } else {
            // Hide all other images
            image.classList.add('media--hidden');
            image.style.display = 'none';
          }
        });

        // Thumbnails use the same data-color as slides; keep them in sync with main gallery.
        this.section.querySelectorAll('.product__thumb').forEach((thumb) => {
          const showThumb = slideMatchesColor(thumb);
          thumb.style.display = showThumb ? '' : 'none';
        });
        
        if (typeof markGalleryLead === 'function') {
          markGalleryLead(productImages);
        }

        console.log('[Siblings] Showing', visibleCount, 'images for color code:', colorCode);
        
        // Make the first visible image active and sync the gallery (required for data-fader-mobile)
        const firstVisible = imagesToShow[0];
        if (firstVisible) {
          firstVisible.classList.remove('media--hidden', 'media--hiding');
          firstVisible.classList.add('media--active');
          const mediaId = firstVisible.getAttribute('data-media-id');
          if (mediaId) {
            productImages.setAttribute('data-active-media', mediaId);
            productImages.dispatchEvent(new CustomEvent('theme:media:select', { detail: { id: mediaId }, bubbles: true }));
          }
        }
      }
    }
    
    // Update legend values (color and size) to reflect current selection
    try {
      // Update color legend (current product block)
      if (variant.option1) {
        const colorLegendEl = this.container.querySelector('[data-current-product] .radio__legend [data-option-value]');
        if (colorLegendEl) {
          const colorValue = typeof variant.option1 === 'string' ? variant.option1.trim() : '';
          const parts = colorValue.split(' ');
          const displayColor = parts.length > 1 ? parts.slice(0, -1).join(' ') : colorValue;
          colorLegendEl.textContent = displayColor;
        }
      }

      // Update size legend
      if (variant.option2) {
        const sizeLegendEl = this.section.querySelector('[data-sibling-sizes] .radio__legend [data-option-value]');
        if (sizeLegendEl) {
          const sizeValue = typeof variant.option2 === 'string' ? variant.option2.trim() : '';
          const sp = sizeValue.split(' ');
          const displaySize = sp.length > 1 ? sp.slice(0, -1).join(' ') : sizeValue;
          sizeLegendEl.textContent = displaySize;
        }
      }
    } catch(e) { /* no-op */ }

    // Update size availability - only when color changes
    if (isColorChange) {
      this.updateSizeAvailability(variant, productData);
    }
  }

  updateSizeAvailability(selectedVariant, productData) {
    const selectedColor = selectedVariant.option1;
    const sizeButtons = this.section.querySelectorAll('[data-sibling-sizes] .radio__button');
    
    console.log('[Siblings] === SIZE AVAILABILITY DEBUG ===');
    console.log('[Siblings] Selected color:', selectedColor);
    console.log('[Siblings] Total variants in product:', productData.variants.length);
    
    // Debug: show all variants for this product
    const colorVariants = productData.variants.filter(v => this.isPricedVariant(v) && v.option1 === selectedColor);
    console.log('[Siblings] Variants matching color "' + selectedColor + '":', colorVariants.length);
    colorVariants.forEach(v => {
      console.log('[Siblings]   - Size:', v.option2, '| Available:', v.available, '| ID:', v.id);
    });
    
    let enabledCount = 0;
    let currentSizeAvailable = false;
    let firstAvailableInput = null;
    const currentlyCheckedInput = this.section.querySelector('[data-sibling-sizes] [data-single-option-selector]:checked');
    const currentSize = currentlyCheckedInput?.value;
    
    sizeButtons.forEach(button => {
      const input = button.querySelector('[data-single-option-selector]');
      if (!input) return;
      
      const size = input.value;
      
      // Find the variant with this color + size combination
      const matchingVariant = productData.variants.find(v =>
        this.isPricedVariant(v) && v.option1 === selectedColor && v.option2 === size
      );
      
      console.log('[Siblings] Checking size:', size);
      console.log('[Siblings]   - Found variant?', !!matchingVariant);
      if (matchingVariant) {
        console.log('[Siblings]   - Variant ID:', matchingVariant.id);
        console.log('[Siblings]   - Available property:', matchingVariant.available);
        console.log('[Siblings]   - Variant full data:', matchingVariant);
      }
      
      if (matchingVariant) {
        // Variant exists for this color + size combination
        button.style.display = '';
        
        if (matchingVariant.available) {
          // Size has stock - enable it
          input.disabled = false;
          input.classList.remove('unavailable');
          input.classList.remove('sold-out');
          enabledCount++;
          
          // Track if current size is available
          if (size === currentSize) {
            currentSizeAvailable = true;
          }
          
          // Track first available size
          if (!firstAvailableInput) {
            firstAvailableInput = input;
          }
          
          console.log('[Siblings] ✓ Size', size, 'ENABLED (in stock)');
        } else {
          // Size exists but sold out - show but disable
          input.disabled = true;
          input.classList.add('unavailable');
          input.classList.add('sold-out');
          console.log('[Siblings] ✗ Size', size, 'DISABLED (sold out)');
        }
      } else {
        // No variant exists for this color + size - hide completely
        button.style.display = 'none';
        input.disabled = true;
        input.classList.add('unavailable');
        console.log('[Siblings] − Size', size, 'HIDDEN (no variant for this color)');
      }
    });
    
    console.log('[Siblings] === SUMMARY: ' + enabledCount + ' sizes enabled ===');
    
    // If current size is not available, select the first available size
    if (!currentSizeAvailable && firstAvailableInput) {
      console.log('[Siblings] Current size "' + currentSize + '" not available, switching to "' + firstAvailableInput.value + '"');
      firstAvailableInput.checked = true;
      
      // Find the new variant with the new size
      const newVariant = productData.variants.find(v =>
        this.isPricedVariant(v) && v.option1 === selectedColor && v.option2 === firstAvailableInput.value
      );
      
      if (newVariant) {
        // Update variant ID input
        const variantIdInput = this.section.querySelector('input[name="id"]');
        if (variantIdInput) {
          variantIdInput.value = newVariant.id;
        }
        
        // Update URL
        if (window.history.replaceState) {
          const newUrl = this.getUrlWithVariant(window.location.href, newVariant.id);
          window.history.replaceState({path: newUrl}, '', newUrl);
        }
        
        // Update product info but NOT size availability (we just did that)
        this.updateProductDOM(newVariant, productData, false);
        this.emitVariantChange(newVariant);
      }
    } else if (currentSizeAvailable) {
      console.log('[Siblings] Current size "' + currentSize + '" is still available');
    } else if (!firstAvailableInput) {
      console.log('[Siblings] No available sizes for this color');
    }
  }

  findSiblingVariant(siblingProductId, colorValue) {
    const siblingWrapper = this.container.querySelector(`[data-sibling-product="${siblingProductId}"]`);
    if (!siblingWrapper) return null;
    
    const siblingDataElement = siblingWrapper.querySelector('[data-sibling-product-json]');
    if (!siblingDataElement) return null;
    
    const siblingProductData = JSON.parse(siblingDataElement.textContent);
    
    // Get current size
    const sizeSelector = this.section.querySelector('[data-sibling-sizes] [data-single-option-selector]:checked') ||
                        this.section.querySelector('[data-sibling-sizes] [data-single-option-selector][type="hidden"]');
    const currentSize = sizeSelector?.value;
    
    // Find matching variant with color + size
    let matchingVariant = siblingProductData.variants.find(v =>
      this.isPricedVariant(v) && v.option1 === colorValue && v.option2 === currentSize
    );
    
    // Fallback: find any variant with the color
    if (!matchingVariant) {
      matchingVariant = siblingProductData.variants.find(v => this.isPricedVariant(v) && v.option1 === colorValue);
    }
    
    return matchingVariant?.id;
  }

  updateActiveStates() {
    const currentProduct = this.container.querySelector('[data-current-product]');
    if (!currentProduct) return;
    
    const swatches = currentProduct.querySelectorAll('[data-sibling-color-swatch]');
    swatches.forEach(swatch => {
      const input = swatch.querySelector('input[data-sibling-color-input]');
      if (input?.checked) {
        swatch.classList.add('swatch__button--selected');
        swatch.setAttribute('aria-current', 'true');
      } else {
        swatch.classList.remove('swatch__button--selected');
        swatch.removeAttribute('aria-current');
      }
    });
  }

  updateSaleBadge(variant) {
    if (!variant) return;
    
    // Check if variant is on sale
    const isOnSale = variant.compare_at_price && 
                     variant.compare_at_price > 0 && 
                     variant.price && 
                     variant.compare_at_price > variant.price;
    
    // Update sale badge visibility
    const saleBadge = this.section.querySelector('[data-sale-badge]') || document.querySelector('[data-sale-badge]');
    if (saleBadge) {
      saleBadge.classList.toggle('hidden', !isOnSale);
    }
    
    // Update product sale wrapper (price off badge)
    const productSaleEl = this.section.querySelector('.product__sale') || document.querySelector('.product__sale');
    if (productSaleEl) {
      productSaleEl.classList.toggle('hidden', !isOnSale);
    }
    
    // Update price off badge and wrapper
    const priceOffWrap = this.section.querySelector('[data-price-off]') || document.querySelector('[data-price-off]');
    if (priceOffWrap) {
      const priceOffBadge = priceOffWrap.querySelector('[data-price-off-badge]');
      if (priceOffBadge) {
        if (isOnSale) {
          // Calculate and display discount percentage
          const discountInt = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          const priceOffAmount = priceOffWrap.querySelector('[data-price-off-amount]');
          if (priceOffAmount) {
            priceOffAmount.innerHTML = `${discountInt}%`;
          }
          priceOffBadge.classList.remove('hidden');
          priceOffWrap.classList.remove('hidden');
        } else {
          priceOffBadge.classList.add('hidden');
          priceOffWrap.classList.add('hidden');
        }
      }
    }
  }

  getUrlWithVariant(url, variantId) {
    if (!url || !variantId) return url;
    if (/variant=/.test(url)) {
      return url.replace(/(variant=)[^&]+/, '$1' + variantId);
    } else if (/\?/.test(url)) {
      return url.concat('&variant=').concat(variantId);
    }
    return url.concat('?variant=').concat(variantId);
  }

  hideUnavailableColors() {
    // Check each sibling product's color swatches
    const siblingWrappers = this.container.querySelectorAll('[data-sibling-product]');
    
    siblingWrappers.forEach(wrapper => {
      const isCurrentProduct = wrapper.hasAttribute('data-current-product');
      const productId = wrapper.dataset.siblingProduct;
      
      // Get product data
      let productData = null;
      
      if (isCurrentProduct) {
        const productDataElement = this.getProductJsonElement();
        if (productDataElement) {
          productData = JSON.parse(productDataElement.textContent);
        }
      } else {
        const siblingDataElement = wrapper.querySelector('[data-sibling-product-json]');
        if (siblingDataElement) {
          productData = JSON.parse(siblingDataElement.textContent);
        }
      }
      
      if (!productData) return;
      
      // Check each color swatch in this product
      const colorSwatches = wrapper.querySelectorAll('[data-sibling-color-swatch]');
      
      colorSwatches.forEach(swatch => {
        const colorValue = swatch.dataset.colorValue;
        
        // Check if any variant with this color is available
        const hasAvailableVariant = productData.variants.some((variant) => {
          return this.isPricedVariant(variant) && variant.option1 === colorValue && variant.available;
        });
        
        if (!hasAvailableVariant) {
          // Hide the swatch and disable the input
          swatch.style.display = 'none';
          const input = swatch.querySelector('input[data-sibling-color-input]');
          if (input) {
            input.disabled = true;
          }
          console.log('[Siblings] Hiding color swatch with no available variants:', colorValue, 'for product:', productId);
        }
      });
    });
  }

  redirectToAvailableVariant() {
    // Get product data
    const productDataElement = this.getProductJsonElement();
    if (!productDataElement) return;
    
    const productData = JSON.parse(productDataElement.textContent);
    
    // Get current variant ID from URL or input
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get('variant');
    const variantIdInput = this.section.querySelector('input[name="id"]');
    const currentVariantId = urlVariantId || variantIdInput?.value;
    
    if (!currentVariantId) {
      // No variant selected, redirect to first available
      const firstAvailable = productData.variants.find(v => v.available);
      if (firstAvailable) {
        console.log('[Siblings] No variant selected, redirecting to first available:', firstAvailable.id);
        const newUrl = this.getUrlWithVariant(window.location.href, firstAvailable.id);
        window.location.href = newUrl;
      }
      return;
    }
    
    // Find the current variant
    const currentVariant = productData.variants.find(v => v.id.toString() === currentVariantId.toString());
    
    if (!currentVariant) {
      console.log('[Siblings] Current variant not found');
      return;
    }
    
    // Check if current variant is available
    if (!currentVariant.available) {
      console.log('[Siblings] Current variant', currentVariantId, 'is not available, finding first available variant');
      
      // Try to find an available variant with the same size
      const currentSize = currentVariant.option2;
      let firstAvailable = null;
      
      if (currentSize) {
        // Try same size, different color
        firstAvailable = productData.variants.find(v => 
          v.available && v.option2 === currentSize
        );
      }
      
      // If no variant with same size, just get first available
      if (!firstAvailable) {
        firstAvailable = productData.variants.find(v => v.available);
      }
      
      if (firstAvailable) {
        console.log('[Siblings] Redirecting to available variant:', firstAvailable.id, '(', firstAvailable.option1, '/', firstAvailable.option2, ')');
        const newUrl = this.getUrlWithVariant(window.location.href, firstAvailable.id);
        window.location.href = newUrl;
      } else {
        console.log('[Siblings] No available variants found for this product');
      }
    } else {
      console.log('[Siblings] Current variant', currentVariantId, 'is available');
    }
  }

  updateInitialSizeAvailability() {
    // Get product data
    const productDataElement = this.getProductJsonElement();
    if (!productDataElement) return;
    
    const productData = JSON.parse(productDataElement.textContent);
    
    // Get current variant ID from URL or input
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get('variant');
    const variantIdInput = this.section.querySelector('input[name="id"]');
    const currentVariantId = urlVariantId || variantIdInput?.value;
    
    if (!currentVariantId) return;
    
    // Find the current variant
    const currentVariant = productData.variants.find(v => v.id.toString() === currentVariantId.toString());
    
    if (!currentVariant) return;
    
    console.log('[Siblings] Updating initial size availability for variant:', currentVariantId, '(', currentVariant.option1, '/', currentVariant.option2, ')');
    
    // Update size availability based on the current color
    this.updateSizeAvailability(currentVariant, productData);
  }

  updateInitialVariantUI() {
    try {
      const productDataElement = this.getProductJsonElement();
      if (!productDataElement) return;
      const productData = JSON.parse(productDataElement.textContent);
      if (!productData || !Array.isArray(productData.variants)) return;

      // Determine current variant ID from URL or hidden input
      const urlParams = new URLSearchParams(window.location.search);
      const urlVariantId = urlParams.get('variant');
      const variantIdInput = this.section.querySelector('input[name="id"]');
      const currentVariantId = urlVariantId || variantIdInput?.value;
      if (!currentVariantId) return;

      let currentVariant = productData.variants.find(v => v.id.toString() === currentVariantId.toString());
      if (!currentVariant || !this.isPricedVariant(currentVariant)) {
        // If URL points at a $0 variant, fall back to first priced variant (prefer available)
        currentVariant =
          productData.variants.find((v) => this.isPricedVariant(v) && v.available) ||
          productData.variants.find((v) => this.isPricedVariant(v));
        if (!currentVariant) return;

        // Update hidden input + URL so the rest of the theme stays consistent
        const variantIdInput = this.section.querySelector('input[name="id"]');
        if (variantIdInput) variantIdInput.value = currentVariant.id;
        if (window.history.replaceState) {
          const newUrl = this.getUrlWithVariant(window.location.href, currentVariant.id);
          window.history.replaceState({ path: newUrl }, '', newUrl);
        }
      }

      // Ensure matching color swatch is checked for current product
      const currentColor = currentVariant.option1;
      if (currentColor) {
        const swatches = this.container.querySelectorAll('[data-current-product] [data-sibling-color-swatch] input[data-sibling-color-input]');
        swatches.forEach(input => {
          if (input.value === currentColor) {
            input.checked = true;
          }
        });
        this.updateActiveStates();
      }

      // Update legends and images to match current variant
      this.updateProductDOM(currentVariant, productData, true);
      this.emitVariantChange(currentVariant);
    } catch(e) { /* no-op */ }
  }

  emitVariantChange(variant) {
    if (!variant || !this.section) return;

    this.section.dispatchEvent(
      new CustomEvent('theme:variant:change', {
        detail: {variant, source: 'siblings'},
        bubbles: true
      })
    );
  }

  isPricedVariant(variant) {
    if (!variant) return false;
    const price = typeof variant.price === 'number' ? variant.price : Number(variant.price);
    const hasPrice = Number.isFinite(price) && price > 0;
    const hasImage = variant.featured_media != null && variant.featured_media !== undefined;
    return hasPrice && hasImage;
  }

  formatMoney(cents) {
    const format = window.theme?.moneyFormat;
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const amount = Number(cents);
    if (format && Number.isFinite(amount)) {
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const value = (amount / 100).toFixed(2);
      const parts = value.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
      const formattedAmount = dollars + (parts[1] ? '.' + parts[1] : '');
      return format.replace(placeholderRegex, formattedAmount);
    }
    const value = amount / 100;
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    });
    return formatter.format(value);
  }
}

window.theme = window.theme || {};
window.theme.initProductSiblingsVariantsIn = function (root) {
  const scope = root && root.nodeType === 1 ? root : document;
  scope.querySelectorAll('[data-product-siblings-variants]').forEach((container) => {
    if (container.dataset.siblingsVariantsBound === 'true') return;
    container.dataset.siblingsVariantsBound = 'true';
    new ProductSiblingsVariants(container);
  });
};

function initProductSiblingsVariants() {
  window.theme.initProductSiblingsVariantsIn(document);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductSiblingsVariants);
} else {
  initProductSiblingsVariants();
}

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('[data-product-siblings-variants]').forEach((el) => {
      delete el.dataset.siblingsVariantsBound;
    });
    initProductSiblingsVariants();
  });
}
